<template>
  <div>
    <ok-cancel-modal
      :title="$t('health.nutritionalPlan.recipesSelector')"
      :ok-button="$t('commons.ok')"
      :cancel-button="$t('commons.cancel')"
      v-on:ok="onOk"
      v-on:cancel="$emit('close')">
      <div slot="body">

        <div class="toolbar">
          <div class="toolbar__search">
            <search-input
              ref="recipeSearch"
              :focus="true"
              v-on:input="onChangeFilter"
            />
          </div>

          <div class="toolbar__actions">
            <button
              class="toolbar__btn"
              :class="{ 'toolbar__btn--active': showCreateRecipe }"
              v-tooltip="$t('health.recipe.new')"
              @click="toggleCreate">
              <font-awesome-icon :icon="faPlusCircle"/>
            </button>

            <button
              class="toolbar__btn"
              :class="{ 'toolbar__btn--active': showNutriRecipes }"
              v-tooltip="$t('health.recipe.myRecipes')"
              @click="toggleMines">
              <font-awesome-icon :icon="faUser"/>
            </button>

            <button
              class="toolbar__btn"
              :class="{ 'toolbar__btn--active': showFavorites }"
              v-tooltip="$t('health.recipe.favorites')"
              @click="toggleFavorites">
              <font-awesome-icon :icon="showFavorites ? faHeartSolid : faHeart"/>
            </button>

            <div class="toolbar__dropdown" v-click-outside="() => showTagFilter = false">
              <button
                class="toolbar__btn"
                :class="{ 'toolbar__btn--active': selectedTags.length > 0 }"
                v-tooltip="$t('health.nutritionalPlan.filterByTag')"
                @click="showTagFilter = !showTagFilter">
                <font-awesome-icon :icon="iconTag"/>
                <span v-if="selectedTags.length > 0" class="toolbar__badge">{{ selectedTags.length }}</span>
              </button>
              <div v-if="showTagFilter" class="toolbar__dropdown-panel">
                <span
                  v-for="tag in allTags"
                  :key="tag.id"
                  class="tag-chip"
                  :class="{ 'tag-chip--active': selectedTags.includes(tag.id) }"
                  @click="toggleTag(tag.id)">
                  {{ tag.name }}
                </span>
              </div>
            </div>

            <select v-model="selectedType" class="toolbar__select">
              <option value="">{{ $t('health.nutritionalPlan.allTypes') }}</option>
              <option v-for="type in allTypes" :key="type" :value="type">
                {{ $t(`health.nutritionalPlan.mealTypes2.${type}`) }}
              </option>
            </select>
          </div>
        </div>

        <create-recipe-modal
          v-if="showCreateRecipe"
          @close="showCreateRecipe = false"
          @created="onRecipeCreated"/>

        <div class="margin-top-l recipe-list">
          <h3 class="margin-bottom-l">{{ $t('health.nutritionalPlan.recipes') }}</h3>
          <div
            v-for="(recipe, index) in getFilteredRecipes.slice(0, maxToShow)"
            :key="index"
            class="recipe-item"
            :class="{ 'recipe-item--selected': isChecked(recipe) }"
            @click="onRecipeSelected(recipe)"
          >
            <img
              v-if="recipe.imageUrl"
              :src="recipe.imageUrl"
              :alt="recipe.name"
              class="recipe-item__image"
            />
            <div class="recipe-item__info">
              <span class="recipe-item__type">{{ $t(`health.nutritionalPlan.mealTypes2.${recipe.type}`) }}</span>
              <span class="recipe-item__name">{{ recipe.name }}</span>
              <div class="recipe-item__macros">
                <span>P: {{ totalMacro(recipe.items, 'protein') }}g</span>
                <span>HC: {{ totalMacro(recipe.items, 'carbs') }}g</span>
                <span>G: {{ totalMacro(recipe.items, 'fat') }}g</span>
                <span>Kcal: {{ totalMacro(recipe.items, 'calories') }}</span>
              </div>
              <div class="recipe-item__tags">
                <span v-for="tag in recipe.tags" :key="tag.id" class="recipe-item__tag">
                  {{ tag.name }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="getFilteredRecipes.length > maxToShow" class="and-x-more">
            {{ $t('variables.andXMore', { count: getFilteredRecipes.length - maxToShow }) }}
          </div>
        </div>

      </div>
    </ok-cancel-modal>
  </div>
</template>

<script>
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import SearchInput from '@/components/shared/SearchInput';
import { faTag, faHeart, faUser } from '@fortawesome/pro-regular-svg-icons';
import { faHeart as faHeartSolid, faPlusCircle } from '@fortawesome/pro-solid-svg-icons';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import recipeApi from '../../api/recipe';
import { mapGetters } from 'vuex';
import CreateRecipeModal from './CreateRecipeModal.vue';

export default {
  name: 'SearchRecipeModal',
  components: {
    OkCancelModal,
    SearchInput,
    FontAwesomeIcon,
    CreateRecipeModal
  },
  props: {
    recipes: {
      type: Array,
      default: () => []
    },
    value: {
      type: Object,
      default: () => null
    },
    initialType:{
      type:String,
      default:''
    }
  },
  data () {
    return {
      recipeFilter: '',
      selectedRecipe: this.value,
      maxToShow: 50,
      selectedTags: [],
      showTagFilter: false,
      showNutriRecipes: false,
      showFavorites: false,
      showCreateRecipe: false,
      selectedType: this.initialType,
      iconTag: faTag,
      faHeart,
      faHeartSolid,
      faPlusCircle,
      faUser,
      favoriteRecipes: [],
      mineRecipes: []
    };
  },
  computed: {
    ...mapGetters(['getLanguageCode']),
    allTypes () {
      return [...new Set(this.recipes.map(r => r.type).filter(Boolean))];
    },
    allTags () {
      const tagsMap = {};
      this.recipes.forEach(recipe => {
        (recipe.tags || []).forEach(tag => {
          tagsMap[tag.id] = tag;
        });
      });
      return Object.values(tagsMap);
    },
    getFilteredRecipes () {
      let result;
      if (this.showNutriRecipes) {
        result = this.mineRecipes;
      } else if (this.showFavorites) {
        result = this.favoriteRecipes;
      } else {
        result = this.recipes;
      }

      if (this.recipeFilter !== '') {
        result = result.filter(recipe =>
          replaceSpecialChars(recipe.name.toUpperCase())
            .includes(replaceSpecialChars(this.recipeFilter.toUpperCase()))
        );
      }

      if (this.selectedTags.length > 0) {
        result = result.filter(recipe =>
          this.selectedTags.every(tagId =>
            (recipe.tags || []).some(tag => tag.id === tagId)
          )
        );
      }

      if (this.selectedType !== '') {
        result = result.filter(recipe => recipe.type === this.selectedType);
      }

      return result;
    }
  },
  methods: {
    isChecked (recipe) {
      return this.selectedRecipe && this.selectedRecipe.id === recipe.id;
    },
    onRecipeSelected (recipe) {
      this.selectedRecipe = JSON.parse(JSON.stringify(recipe));
    },
    onOk () {
      if (this.selectedRecipe) {
        this.$emit('input', this.selectedRecipe);
      }
      this.$emit('close');
    },
    onChangeFilter (filter) {
      this.recipeFilter = filter;
    },
    totalMacro (items, macro) {
      if (!items) return 0;
      return items.reduce((sum, item) => sum + (item[macro] || 0), 0).toFixed(1);
    },
    toggleTag (tagId) {
      const index = this.selectedTags.indexOf(tagId);
      if (index === -1) {
        this.selectedTags.push(tagId);
      } else {
        this.selectedTags.splice(index, 1);
      }
    },
    async toggleFavorites () {
      if (!this.showFavorites) {
        this.favoriteRecipes = await recipeApi.getFavoriteRecipes(this, this.getLanguageCode);
      }
      this.showFavorites = !this.showFavorites;
      this.showNutriRecipes = false;
    },
    async toggleMines () {
      if (!this.showNutriRecipes) {
        this.mineRecipes = await recipeApi.getRecipesByNutritionist(this, this.getLanguageCode);
      }
      this.showNutriRecipes = !this.showNutriRecipes;
      this.showFavorites = false;
    },
    toggleCreate () {
      this.showCreateRecipe = !this.showCreateRecipe;
    },
    onRecipeCreated () {
      this.showCreateRecipe = false;
      if (this.showNutriRecipes) {
        recipeApi.getRecipesByNutritionist(this, this.getLanguageCode).then(data => {
          this.mineRecipes = data;
        });
      }
    }
  }
};

const replaceSpecialChars = (str) => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/([^\w]+|\s+)/g, '-')
    .replace(/(^-+|-+$)/g, '');
};
</script>

<style lang="scss" scoped>
@import "src/stylesheets/variables";

.toolbar {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75em;

  &__search {
    flex: 1;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 0.5em;
    flex-shrink: 0;
  }

  &__btn {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.2rem;
    height: 2.2rem;
    background: $black-light8;
    border: 1px solid $black-light6;
    border-radius: $radius;
    cursor: pointer;
    font-size: 1.1rem;
    color: $black-light3;
    transition: border-color 0.15s, color 0.15s, background 0.15s;

    &:hover {
      border-color: $primary;
      color: $primary;
    }

    &--active {
      background: rgba($primary, 0.1);
      border-color: $primary;
      color: $primary;
    }
  }

  &__badge {
    position: absolute;
    top: -0.35rem;
    right: -0.35rem;
    background: $primary;
    color: $white;
    border-radius: 50%;
    font-size: 0.6rem;
    width: 1rem;
    height: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
  }

  &__dropdown {
    position: relative;
  }

  &__dropdown-panel {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 20;
    display: flex;
    flex-wrap: wrap;
    gap: 0.4em;
    padding: 0.75em;
    background: $white;
    border: 1px solid $black-light6;
    border-radius: $radius;
    min-width: 200px;
    box-shadow: 0 4px 12px rgba($black, 0.1);
  }

  &__select {
    height: 2.2rem;
    padding: 0 0.75em;
    border: 1px solid $black-light6;
    border-radius: $radius;
    background: $black-light8;
    cursor: pointer;
    font-size: 0.85em;
    color: $black-light3;

    &:hover { border-color: $primary; }
    &:focus { outline: none; border-color: $primary; }
  }
}

/* ── Tag chips ── */
.tag-chip {
  background: $black-light8;
  border: 1px solid $black-light6;
  border-radius: 12px;
  padding: 0.2em 0.8em;
  font-size: 0.8em;
  cursor: pointer;

  &--active {
    background: $primary;
    color: $white;
    border-color: $primary;
  }

  &:hover { border-color: $primary; }
}

/* ── Recipe list ── */
.recipe-list {
  min-height: calc(100vh / 2);
  max-height: calc(100vh / 2);
  overflow-y: auto;
}

.recipe-item {
  display: flex;
  flex-direction: row;
  border: 1px solid $black-light6;
  border-radius: $radius;
  margin-bottom: 0.75em;
  cursor: pointer;
  overflow: hidden;

  &:hover {
    background: rgba($primary, 0.05);
    border-color: $primary;
  }

  &--selected {
    border: 2px solid $primary;
    background: rgba($primary, 0.1);
  }

  &__image {
    width: 50%;
    height: 140px;
    object-fit: cover;
    flex-shrink: 0;
  }

  &__info {
    width: 50%;
    padding: 0.75em;
    display: flex;
    flex-direction: column;
    gap: 0.25em;
  }

  &__type {
    font-size: 0.8em;
    color: $black-light5;
  }

  &__name {
    font-weight: bold;
    font-size: 0.95em;
  }

  &__macros {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5em;
    font-size: 0.78em;
    color: $black-light4;
    margin-top: auto;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25em;
  }

  &__tag {
    background: rgba($primary, 0.1);
    color: $primary;
    border-radius: 12px;
    padding: 0.1em 0.5em;
    font-size: 0.75em;
  }
}

.and-x-more {
  color: $black-light5;
  text-align: center;
  padding: 0.5em;
}
</style>



// WEBPACK FOOTER //
// src/brand/health/components/nutritionPlan/AddRecipeModal.vue